import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from '../utils/axiosConfig';
import { useQueryClient } from '@tanstack/react-query';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  const fetchUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
    
    const handleUnauthorized = () => {
      setUser(null);
      localStorage.removeItem('token');
    };
    window.addEventListener('auth-unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth-unauthorized', handleUnauthorized);
  }, []);

  const login = async (email, password) => {
    const response = await axios.post('/auth/login', { email, password });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    setUser(response.data.user);
    return response.data;
  };

  const register = async (formData) => {
    const response = await axios.post('/auth/register', formData);
    return response.data;
  };

  const verifyEmail = async (userId, code) => {
    const response = await axios.post('/auth/verify-email', { userId, code });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    if (response.data.user) {
      setUser(response.data.user);
    }
    return response.data;
  };

  const resendVerification = async (userId) => {
    const response = await axios.post('/auth/resend-verification', { userId });
    return response.data;
  };

  const googleLogin = async (credential) => {
    const response = await axios.post('/auth/google', { credential });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.clear();
    setUser(null);
    queryClient.clear();
    try {
      await axios.post('/auth/logout');
    } catch (err) {
      // Ignore network/server errors during logout
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, loading, 
      login, register, verifyEmail, resendVerification, googleLogin, logout, 
      setUser, 
      isAuthenticated: !!user 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
